import type { AlphaRunDesign } from "@nlc/shared-types";

import { Check, Search } from "lucide-react";
import { Button as RACButton, ToggleButton } from "react-aria-components";

import { Button } from "@/components/button";
import { cn } from "@/utils/cn";

type AlphaDesignCardProps = {
  design: AlphaRunDesign;
  failedMessage: string;
  isSelected: boolean;
  name: string;
  onEdit: () => void;
  onPreview: () => void;
  onToggleSelected: () => void;
  styleTag: string;
};

export function AlphaDesignCard({
  design,
  failedMessage,
  isSelected,
  name,
  onEdit,
  onPreview,
  onToggleSelected,
  styleTag,
}: AlphaDesignCardProps) {
  const { imageUrl } = design;
  const isFailed = !imageUrl;

  return (
    <div
      className={cn(
        "flex flex-col gap-xs rounded-xl border bg-bg-primary px-md pt-md",
        isSelected
          ? "border-2 border-border-brand bg-bg-brand-primary"
          : "border-border-secondary",
      )}
      data-testid="alpha-design-card"
    >
      <div className="relative aspect-square overflow-hidden rounded-lg bg-bg-secondary">
        {isFailed ? (
          <p className="grid size-full place-items-center p-lg text-center text-sm text-text-tertiary">
            {failedMessage}
          </p>
        ) : (
          <>
            <ToggleButton
              aria-label={`Select ${name}`}
              className="size-full cursor-pointer outline-hidden focus-visible:ring-2 focus-visible:ring-border-brand"
              isSelected={isSelected}
              onChange={onToggleSelected}
            >
              <img alt="" className="size-full object-cover" src={imageUrl} />
            </ToggleButton>
            {isSelected && (
              <span
                aria-hidden
                className="pointer-events-none absolute start-md top-md grid size-7 place-items-center rounded-full border-2 border-white bg-bg-brand-solid text-white"
              >
                <Check size={14} />
              </span>
            )}
            <RACButton
              aria-label={`View ${name} full size`}
              className="absolute end-xs top-xs grid size-11 cursor-pointer place-items-center outline-hidden"
              onPress={onPreview}
            >
              <span className="grid size-[30px] place-items-center rounded-full bg-black/70 text-white">
                <Search size={16} />
              </span>
            </RACButton>
          </>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-text-tertiary">{styleTag}</span>
        {!isFailed && (
          <Button
            aria-label={`Edit ${name}`}
            color="color"
            onPress={onEdit}
            size="md"
            variant="tertiary"
          >
            Edit
          </Button>
        )}
      </div>
    </div>
  );
}
